import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { paginationRequestSchema } from '@/lib/pagination.js';
import {
  classEligibilitySchema,
  coachOverallGradeSchema,
  coachStatusSchema,
  coachSubStatusSchema,
  coachTqReviewOpenStatusSchema,
  coachTqReviewTypeSchema,
  getCoachesTqReviewSortOrderSchema,
  normalizeCoachTqReviewType,
} from '@airreading/common/data';

const optionalPlainDateSchema = z
  .string()
  .refine((value) => {
    try {
      Temporal.PlainDate.from(value);
      return true;
    } catch {
      return false;
    }
  })
  .optional()
  .catch(undefined);

/**
 * Fold a bookmarked review type onto the current public enum.
 * Retired QR/FR values map to Quick/Focus. Anything else is left unchanged so
 * the enum schema fails and `.catch` can drop that entry.
 */
function tryNormalizeCoachTqReviewType(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return normalizeCoachTqReviewType(value);
  } catch {
    return value;
  }
}

function searchEnumItem<ItemSchema extends z.ZodTypeAny>(itemSchema: ItemSchema) {
  return itemSchema.optional().catch(undefined);
}

const reviewTypeSearchItemSchema = z.preprocess(tryNormalizeCoachTqReviewType, searchEnumItem(coachTqReviewTypeSchema));

function optionalLenientArray<ItemSchema extends z.ZodTypeAny>(itemSchema: ItemSchema) {
  return z
    .array(itemSchema)
    .optional()
    .catch(undefined)
    .transform((values): Array<Exclude<z.output<ItemSchema>, undefined>> | undefined => {
      if (!values?.length) return undefined;

      const seen = new Set<Exclude<z.output<ItemSchema>, undefined>>();
      const result: Array<Exclude<z.output<ItemSchema>, undefined>> = [];
      for (const value of values) {
        if (value === undefined) continue;
        const kept = value as Exclude<z.output<ItemSchema>, undefined>;
        if (seen.has(kept)) continue;
        seen.add(kept);
        result.push(kept);
      }

      return result.length > 0 ? result : undefined;
    });
}

export const tqReviewSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  reviewTypes: optionalLenientArray(reviewTypeSearchItemSchema),
  statuses: optionalLenientArray(searchEnumItem(coachTqReviewOpenStatusSchema)),
  overallGrades: optionalLenientArray(searchEnumItem(coachOverallGradeSchema)),
  classEligibilities: optionalLenientArray(searchEnumItem(classEligibilitySchema)),
  coachStatuses: optionalLenientArray(searchEnumItem(coachStatusSchema)),
  coachSubStatuses: optionalLenientArray(searchEnumItem(coachSubStatusSchema)),
  lastReviewDateFrom: optionalPlainDateSchema,
  lastReviewDateTo: optionalPlainDateSchema,
  mostRecentSessionDateFrom: optionalPlainDateSchema,
  mostRecentSessionDateTo: optionalPlainDateSchema,
  sortBy: getCoachesTqReviewSortOrderSchema.optional(),
});
